import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EiqButton } from 'eiq-storybook';

import Table from 'src/components/Table';
import { useDeleteJobExternalStakeholder } from 'src/hooks/jobExternalStakeholder/useDeleteJobExternalStakeholder';
import { useExternalJobStakeholders } from 'src/hooks/jobExternalStakeholder/useJobExternalStakeholders';
import { Popup } from 'src/components/Popup';
import { notification } from 'src/components/Notification';
import s from './SiteRepresentative.styles';

const SiteRepresentative = ({
  isEditable,
  jobId,
  serviceType,
  triggerRefetchJobExternalStakeholders,
  setJobExternalStakeholdersData,
  setIsLoadingJobExternalStakeholders,
  showInvitePopupWindow,
  className,
  redirect,
  currentUserId
  }) => {
  const { t } = useTranslation();
  const [jobExternalStakeholders, setJobExternalStakeholders] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  
  const {
    data: jobExternalStakeholdersData,
    loading: isLoadingJobExternalStakeholders,
    refetch: refetchJobExternalStakeholders
  } = useExternalJobStakeholders(jobId, serviceType);

  useEffect(() => {
    if (triggerRefetchJobExternalStakeholders) {
      refetchJobExternalStakeholders();
    }
  }, [triggerRefetchJobExternalStakeholders]);

  useEffect(() => {
    const data = jobExternalStakeholdersData ? jobExternalStakeholdersData?.jobExternalStakeholders : [];
    setJobExternalStakeholders(data);
    setJobExternalStakeholdersData(data);
  }, [jobExternalStakeholdersData]);

  useEffect(() => {
    setIsLoadingJobExternalStakeholders(isLoadingJobExternalStakeholders);
  }, [isLoadingJobExternalStakeholders]);

  const {
    isLoading: isDeletingJobExternalStakeholder,
    deleteJobExternalStakeholderCallback: deleteJobExternalStakeholder,
  } = useDeleteJobExternalStakeholder();

  const deleteSiteRepresentative = async (row) => {
    const response = await deleteJobExternalStakeholder({
      id: row.original?.id,
    });

    if (response) {
      if (currentUserId === row?.original?.userId) { 
        redirect();
      } else {
        setSelectedRow(null);
        refetchJobExternalStakeholders();
      }
      notification.success(t('USER_REMOVED_SUCCESSFULLY'));
    }
  }

  const renderCrossIcon = (row) => {
    return (
      <>
        {isEditable && 
          <s.DeleteSiteRepresentativeIcon
            name="cross"
            style={{ width: '12px', height: '12px', cursor: 'pointer' }}
            disabled={isDeletingJobExternalStakeholder || isLoadingJobExternalStakeholders}
            onClick={() => setSelectedRow(row)}
          />
        }
      </>
    )
  };

  const crossIcon = [
    {
      Header: '',
      id: 'cross',
      show: true,
      width: 40,
      accessor: 'user.id',
      Cell: ({ row }) => renderCrossIcon(row),
      disableSortBy: true,
    }
  ];

  const siteRepresentativeColumns = [
    {
      Header: `${t('COLUMN_NAME')}`,
      id: 'name',
      accessor: (row) => `${row.user.firstName} ${row.user.lastName}`,
      show: true,
      disableSortBy: true,
    },
    {
      Header: `${t('COLUMN_EMAIL')}`,
      id: 'email',
      accessor: 'user.email',
      width: 250,
      show: true,
      disableSortBy: true,
    },
    {
      Header: `${t('COLUMN_ROLE')}`,
      id: 'role',
      accessor: 'role',
      show: true,
      disableSortBy: true,
    },
    {
      Header: `${t('COLUMN_STATUS')}`,
      id: 'status',
      accessor: (row) => row.user.active ? 'Active' : 'Inactive',
      width: 125,
      show: true,
      disableSortBy: true,
    }
  ];

  return (<>
    <s.ExternalJobTable className={className}>
      <s.TableTitle>
        {t('SITE_REPRESENTATIVE')}
      </s.TableTitle>
      <Table
        header={() => (
          <s.TableHeader>
            {isEditable && (<EiqButton
              variant='btn--outline-link'
              label={t('INVITE')}
              onClick={showInvitePopupWindow}
            />
            )}
          </s.TableHeader>
        )}
        gridColumns={[...siteRepresentativeColumns, ...crossIcon]}
        data={jobExternalStakeholders || []}
        hideDeletedButton={true}
        showColumnSettings={false}
        showPagination={false}
      />
    </s.ExternalJobTable>
    {
      selectedRow && <s.ModalWrapper>
        <Popup
          withOverflow
          isCloseHidden
          className="small-container"
        >
        <div className="saq-modal-body">
          <p>{t('USER_DELETE_CONFIRMATION')}</p>
        </div>
        <s.ModalButtonWrapper>
          <EiqButton 
            variant='btn--outline-link'
            label={t('CANCEL')}
            onClick={() => setSelectedRow(null)}
          />
          <EiqButton
            label={t('YES')}
            onClick={async (e) => {
              e.preventDefault();
              deleteSiteRepresentative(selectedRow);
            }}
          />
        </s.ModalButtonWrapper>
        </Popup>
      </s.ModalWrapper>
    }
  </>
  )
};

export default React.memo(SiteRepresentative);
